// Opens a post's gallery images (<div class="gallery">, see
// trix_editor/gallery.ts) full size in a dialog, stepping through the rest of
// that gallery with the arrow keys or buttons. Galleries inside the editor are
// left alone so clicking there still selects the image.

const EDITOR = ".ProseMirror";

function element<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  className: string,
  text?: string,
): HTMLElementTagNameMap[K] {
  const el = document.createElement(tag);
  el.className = className;
  if (text) el.textContent = text;
  return el;
}

function button(className: string, label: string, text: string) {
  const btn = element("button", className, text);
  btn.type = "button";
  btn.setAttribute("aria-label", label);
  return btn;
}

function buildLightbox() {
  const dialog = element("dialog", "lightbox");
  dialog.setAttribute("aria-label", "Image viewer");

  const figure = element("figure", "lightbox-figure");
  const image = element("img", "lightbox-image");
  const caption = element("figcaption", "lightbox-caption");
  figure.append(image, caption);

  const counter = element("p", "lightbox-counter");
  const close = button("lightbox-close", "Close", "×");
  const prev = button("lightbox-prev", "Previous image", "‹");
  const next = button("lightbox-next", "Next image", "›");

  dialog.append(figure, counter, prev, next, close);
  document.body.append(dialog);

  return { dialog, image, caption, counter, close, prev, next };
}

export default function setupGalleryLightbox() {
  const galleryImages = (img: HTMLImageElement) =>
    Array.from(
      img.closest(".gallery")?.querySelectorAll<HTMLImageElement>("img") ?? [],
    );

  const isViewable = (target: EventTarget | null): target is HTMLImageElement =>
    target instanceof HTMLImageElement &&
    target.closest(".gallery") !== null &&
    target.closest(EDITOR) === null;

  const viewable = Array.from(
    document.querySelectorAll<HTMLImageElement>(".gallery img"),
  ).filter(isViewable);
  if (viewable.length === 0) return;

  // Make the images reachable and announced as controls for keyboard users.
  viewable.forEach((img) => {
    img.tabIndex = 0;
    img.setAttribute("role", "button");
    img.setAttribute(
      "aria-label",
      img.alt ? `View image: ${img.alt}` : "View image",
    );
  });

  let lightbox: ReturnType<typeof buildLightbox> | null = null;
  let images: HTMLImageElement[] = [];
  let index = 0;
  let opener: HTMLImageElement | null = null;

  const show = (i: number) => {
    if (!lightbox) return;
    index = (i + images.length) % images.length;
    const img = images[index];

    lightbox.image.src = img.currentSrc || img.src;
    lightbox.image.alt = img.alt;
    lightbox.caption.textContent = img.alt;
    lightbox.caption.hidden = !img.alt;
    lightbox.counter.textContent = `${index + 1} / ${images.length}`;

    const single = images.length < 2;
    lightbox.prev.hidden = single;
    lightbox.next.hidden = single;
    lightbox.counter.hidden = single;
  };

  const open = (img: HTMLImageElement) => {
    if (!lightbox) {
      lightbox = buildLightbox();
      const { dialog, close, prev, next } = lightbox;

      close.addEventListener("click", () => dialog.close());
      prev.addEventListener("click", () => show(index - 1));
      next.addEventListener("click", () => show(index + 1));

      // A click on the backdrop lands on the dialog itself.
      dialog.addEventListener("click", (e) => {
        if (e.target === dialog) dialog.close();
      });

      dialog.addEventListener("keydown", (e) => {
        if (e.key === "ArrowLeft") show(index - 1);
        if (e.key === "ArrowRight") show(index + 1);
      });

      dialog.addEventListener("close", () => opener?.focus());
    }

    opener = img;
    images = galleryImages(img);
    show(images.indexOf(img));
    lightbox.dialog.showModal();
  };

  document.addEventListener("click", (e) => {
    if (isViewable(e.target)) open(e.target);
  });

  document.addEventListener("keydown", (e) => {
    if ((e.key === "Enter" || e.key === " ") && isViewable(e.target)) {
      e.preventDefault();
      open(e.target);
    }
  });
}
